import { Injectable, NgZone } from "@angular/core";
import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import { ToastrService } from "ngx-toastr";
import { EMPTY, Subscription, forkJoin, interval, of } from "rxjs";
import { catchError, distinctUntilChanged, finalize, switchMap, take } from "rxjs/operators";
import SockJS from "sockjs-client/dist/sockjs";
import { AuthStore } from "src/app/services/auth/auth-store.service";
import { TokenService } from "src/app/services/auth/token.service";
import { NotificationResponse } from "src/app/shared/models/notification-response.model";
import { environment } from "src/environment/environment";
import { NotificationApiService } from "./notification-api.service";
import { NotificationStoreService } from "./notification-store.service";

// Fallback polling is used only while the WebSocket is disconnected.
// Do not add connected-state polling here; a healthy socket should stay idle until the backend pushes an event.
const POLLING_INTERVAL_MS = 60_000;
const SOCKET_RECONNECT_DELAY_MS = 10_000;
const SOCKET_CONNECT_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: "root" })
export class NotificationRealtimeService {
  private client?: Client;
  private activeToken: string | null = null;
  private startingToken: string | null = null;
  private pollingSub?: Subscription;
  private authSub?: Subscription;
  private connectTimeoutId?: ReturnType<typeof setTimeout>;
  private readonly socketSubs: StompSubscription[] = [];

  constructor(
    private readonly authStore: AuthStore,
    private readonly notificationApi: NotificationApiService,
    private readonly notificationStore: NotificationStoreService,
    private readonly toastr: ToastrService,
    private readonly zone: NgZone,
    private readonly tokenService: TokenService,
  ) {}

  start(): void {
    if (this.authSub) {
      return;
    }

    this.authSub = this.authStore.state$.pipe(
      distinctUntilChanged((previous, current) =>
        previous.isLoggedIn === current.isLoggedIn && previous.accessToken === current.accessToken
      ),
      switchMap((state) => {
        if (!state.isLoggedIn || !state.accessToken) {
          this.disconnect();
          this.notificationStore.clear();
          return EMPTY;
        }

        return of(state.accessToken);
      }),
    ).subscribe((token) => {
      this.startSession(token);
    });

    const existingToken = this.tokenService.getAccessToken();
    if (existingToken) {
      this.startSession(existingToken);
    }
  }

  stop(): void {
    this.authSub?.unsubscribe();
    this.authSub = undefined;
    this.disconnect();
    this.notificationStore.clear();
  }

  refreshNow(): void {
    if (!this.hasActiveToken()) {
      this.notificationStore.clear();
      return;
    }

    this.loadInitialState().pipe(take(1)).subscribe();
  }

  private startSession(token: string): void {
    if (this.activeToken === token || this.startingToken === token) {
      return;
    }

    this.startingToken = token;
    this.loadInitialState().pipe(
      take(1),
      finalize(() => {
        this.startingToken = null;
      }),
    ).subscribe({
      next: () => this.connect(token),
      error: () => this.connect(token),
    });
  }

  private connect(token: string): void {
    if (this.client?.active && this.activeToken === token) {
      return;
    }

    this.disconnectSocketOnly();
    this.activeToken = token;
    this.notificationStore.setConnectionStatus("connecting");
    this.logDev("Notification socket connecting", { url: this.resolveWebSocketUrl() });

    this.client = new Client({
      webSocketFactory: () => new SockJS(this.resolveWebSocketUrl()),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: SOCKET_RECONNECT_DELAY_MS,
      debug: (message) => this.logStompDebug(message),
      onConnect: () => {
        this.zone.run(() => {
          this.clearConnectTimeout();
          this.stopPolling();
          this.notificationStore.setConnectionStatus("connected");
          this.logDev("Notification socket connected");
          this.subscribeToUserQueues();
        });
      },
      onStompError: () => {
        this.zone.run(() => {
          this.logDev("Notification socket STOMP error");
          this.startPolling();
        });
      },
      onWebSocketError: () => {
        this.zone.run(() => {
          this.logDev("Notification socket transport error");
          this.startPolling();
        });
      },
      onWebSocketClose: () => {
        this.zone.run(() => {
          if (this.activeToken) {
            this.logDev("Notification socket closed; polling until reconnect");
            this.startPolling();
          }
        });
      },
    });

    this.client.activate();
    this.startConnectTimeout();
  }

  private subscribeToUserQueues(): void {
    if (!this.client?.connected) {
      return;
    }

    this.clearSocketSubscriptions();

    this.socketSubs.push(
      this.client.subscribe("/user/queue/notifications", (message) => {
        this.logDev("notification realtime received", { destination: "/user/queue/notifications" });
        this.zone.run(() => this.handleNotificationMessage(message));
      }),
      this.client.subscribe("/user/queue/unread-count", (message) => {
        this.logDev("unread count realtime received", { destination: "/user/queue/unread-count" });
        this.zone.run(() => this.handleUnreadCountMessage(message));
      }),
    );
    this.logDev("Notification socket subscribed to user queues");
  }

  private handleNotificationMessage(message: IMessage): void {
    const notification = this.parseJson<NotificationResponse>(message.body);
    if (!notification?.id) {
      return;
    }

    if (!this.isNotificationForCurrentUser(notification)) {
      this.logDev("Notification socket message ignored for different recipient", { id: notification.id });
      return;
    }

    this.logDev("Notification socket message received", { id: notification.id, type: notification.type });
    const isNew = this.notificationStore.prependNotification(notification);
    if (isNew && !notification.read) {
      this.notificationStore.setUnreadCount(this.notificationStore.unreadCount + 1);
    }

    if (isNew && document.visibilityState === "visible") {
      this.toastr.info(notification.message, notification.title || "Notification");
    }
  }

  private handleUnreadCountMessage(message: IMessage): void {
    if (!this.hasActiveToken()) {
      return;
    }

    const response = this.parseJson<any>(message.body);
    const count = response?.data?.count ?? response?.count ?? response;
    this.logDev("Notification unread count received", { count: Number(count) });
    this.notificationStore.setUnreadCount(Number(count));
  }

  private loadInitialState() {
    if (!this.hasActiveToken()) {
      this.notificationStore.clear();
      return of(true);
    }

    return forkJoin({
      notifications: this.notificationApi.getNotifications({ page: 0, size: 20 }).pipe(
        catchError(() => of(null)),
      ),
      unreadCount: this.notificationApi.getUnreadCount().pipe(
        catchError(() => of(null)),
      ),
    }).pipe(
      switchMap(({ notifications, unreadCount }) => {
        if (notifications) {
          this.notificationStore.setNotifications(this.normalizeNotificationList(notifications));
        }
        if (unreadCount) {
          this.notificationStore.setUnreadCount(Number((unreadCount as any)?.data?.count ?? 0));
        }
        return of(true);
      }),
    );
  }

  private startPolling(): void {
    if (this.pollingSub || !this.activeToken) {
      return;
    }

    this.logDev("Notification fallback polling started");
    this.notificationStore.setConnectionStatus("polling");
    this.loadInitialState().pipe(take(1)).subscribe();
    this.pollingSub = interval(POLLING_INTERVAL_MS).subscribe(() => {
      this.loadInitialState().pipe(take(1)).subscribe();
    });
  }

  private stopPolling(): void {
    if (this.pollingSub) {
      this.logDev("Notification fallback polling stopped");
    }
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  private disconnect(): void {
    this.activeToken = null;
    this.startingToken = null;
    this.stopPolling();
    this.disconnectSocketOnly();
    this.notificationStore.setConnectionStatus("disconnected");
  }

  private disconnectSocketOnly(): void {
    this.clearConnectTimeout();
    this.clearSocketSubscriptions();
    this.client?.deactivate();
    this.client = undefined;
  }

  private clearSocketSubscriptions(): void {
    while (this.socketSubs.length) {
      this.socketSubs.pop()?.unsubscribe();
    }
  }

  private resolveWebSocketUrl(): string {
    return `${environment.apiUrl.replace(/\/$/, "")}/ws`;
  }

  private startConnectTimeout(): void {
    this.clearConnectTimeout();
    this.connectTimeoutId = setTimeout(() => {
      this.zone.run(() => {
        if (this.client?.connected) {
          return;
        }

        this.logDev("Notification socket connect timeout; polling until reconnect");
        this.startPolling();
      });
    }, SOCKET_CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeoutId) {
      clearTimeout(this.connectTimeoutId);
      this.connectTimeoutId = undefined;
    }
  }

  private normalizeNotificationList(response: any): NotificationResponse[] {
    const data = response?.data ?? response;
    const notifications = data?.content ?? data?.notifications ?? data;
    return Array.isArray(notifications) ? notifications : [];
  }

  private parseJson<T>(value: string): T | null {
    try {
      return JSON.parse(value) as T;
    } catch {
      this.logDev("Notification socket message JSON parse failed");
      return null;
    }
  }

  private logDev(message: string, detail?: unknown): void {
    if (!environment.production) {
      console.debug(message, detail ?? "");
    }
  }

  private logStompDebug(message: string): void {
    if (environment.production) {
      return;
    }

    console.log("[STOMP]", this.redactSensitiveDebug(message));
  }

  private redactSensitiveDebug(message: string): string {
    return message.replace(/Authorization:\s*Bearer\s+[^\n\r]+/gi, "Authorization: Bearer [redacted]");
  }

  private hasActiveToken(): boolean {
    return !!(this.activeToken || this.startingToken || this.tokenService.getAccessToken());
  }

  private isNotificationForCurrentUser(notification: NotificationResponse): boolean {
    const currentUserId = this.getCurrentUserIdFromToken();
    return currentUserId === null || notification.recipientUserId === currentUserId;
  }

  private getCurrentUserIdFromToken(): number | null {
    const token = this.activeToken || this.startingToken || this.tokenService.getAccessToken();
    const payload = this.decodeTokenPayload(token);
    const id = payload?.["userId"] ?? payload?.["id"] ?? payload?.["uid"];
    const numericId = Number(id);
    return Number.isFinite(numericId) ? numericId : null;
  }

  private decodeTokenPayload(token: string | null): Record<string, unknown> | null {
    if (!token) {
      return null;
    }

    try {
      const encodedPayload = token.split(".")[1];
      if (!encodedPayload) {
        return null;
      }

      return JSON.parse(atob(encodedPayload)) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
}
